/**
 * documentCache.js
 *
 * Framework-agnostic localStorage LRU cache for instant document rendering.
 *
 * A single localStorage key holds a map of `{ [documentId]: entry }`, where
 *   entry = {
 *     documentId,
 *     documentData,      // the document-data fetch payload (content/milepadData/etc.)
 *     documentInfo,      // the auth payload that drives the Header (rightType, members…)
 *     lastAccessedAt,    // bumped on every read — drives LRU eviction
 *     updatedAt,         // bumped on every write
 *   }
 *
 * Design rules:
 *  - NEVER throws. Every localStorage access is wrapped in try/catch and degrades
 *    to a no-op so a corrupt/unavailable store can never break rendering.
 *  - At most MAX_CACHED_DOCUMENTS entries are retained (LRU by `lastAccessedAt`).
 */

const PROJECT_SHORT_NAME = "milepad";
export const CACHE_KEY = `cache_documents_${PROJECT_SHORT_NAME}`;

export const MAX_CACHED_DOCUMENTS = 5;

// Legacy key names that may linger in users' localStorage from earlier iterations.
// Removed once on module load so they never accumulate.
export const LEGACY_CACHE_KEYS = [
  "cache_documents",
  "document_cache",
  "milepad_document_cache",
  "cached_documents",
];

const isBrowser = () =>
  typeof window !== "undefined" && !!window.localStorage;

/** Read and parse the whole cache map. Always returns a plain object. */
const readCache = () => {
  if (!isBrowser()) return {};
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

/** Persist the whole cache map. No-op on failure. */
const writeCache = (cache) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    // Quota exceeded / serialization error / private mode — degrade silently.
  }
};

/**
 * An entry is "renderable" only when it carries the content payload needed to
 * paint the editor immediately. documentInfo alone is not enough.
 */
const isRenderableEntry = (entry) =>
  !!entry &&
  !!entry.documentData &&
  typeof entry.documentData === "object";

/** Enforce the LRU cap, evicting the lowest `lastAccessedAt` entries. */
const enforceLruLimit = (cache) => {
  const ids = Object.keys(cache);
  if (ids.length <= MAX_CACHED_DOCUMENTS) return cache;

  const sortedByAccess = ids.sort(
    (a, b) => (cache[a]?.lastAccessedAt || 0) - (cache[b]?.lastAccessedAt || 0)
  );
  const evictCount = ids.length - MAX_CACHED_DOCUMENTS;
  for (let i = 0; i < evictCount; i += 1) {
    delete cache[sortedByAccess[i]];
  }
  return cache;
};

/**
 * Cheap, NON-mutating existence check. Safe to call during render.
 * Returns true only when a renderable entry exists for the id.
 */
export const hasCachedDocument = (id) => {
  if (!id) return false;
  try {
    const cache = readCache();
    return isRenderableEntry(cache[id]);
  } catch (e) {
    return false;
  }
};

/**
 * Returns the entry for `id` and bumps its `lastAccessedAt` (a read counts as
 * an access for LRU purposes), then persists. Returns null when absent.
 */
export const getCachedDocument = (id) => {
  if (!id) return null;
  try {
    const cache = readCache();
    const entry = cache[id];
    if (!entry) return null;

    const bumped = { ...entry, lastAccessedAt: Date.now() };
    cache[id] = bumped;
    writeCache(cache);
    return bumped;
  } catch (e) {
    return null;
  }
};

/**
 * Upsert an entry. Undefined fields fall back to the existing entry so a
 * partial write never wipes data. Refreshes timestamps, then enforces LRU.
 */
export const setCachedDocument = (id, { documentData, documentInfo } = {}) => {
  if (!id) return;
  try {
    const cache = readCache();
    const existing = cache[id] || {};
    const now = Date.now();

    cache[id] = {
      documentId: id,
      documentData:
        documentData !== undefined ? documentData : existing.documentData,
      documentInfo:
        documentInfo !== undefined ? documentInfo : existing.documentInfo,
      lastAccessedAt: now,
      updatedAt: now,
    };

    enforceLruLimit(cache);
    writeCache(cache);
  } catch (e) {
    // no-op
  }
};

/**
 * Returns `{ documentData, documentInfo }` for instant rendering, or null.
 * Counts as an access (bumps LRU) via getCachedDocument.
 */
export const getMergedCachedData = (id) => {
  const entry = getCachedDocument(id);
  if (!isRenderableEntry(entry)) return null;
  return {
    documentData: entry.documentData,
    documentInfo: entry.documentInfo || null,
  };
};

/** Remove a single entry (e.g. when it becomes invalid). No-op on failure. */
export const removeCachedDocument = (id) => {
  if (!id) return;
  try {
    const cache = readCache();
    if (cache[id]) {
      delete cache[id];
      writeCache(cache);
    }
  } catch (e) {
    // no-op
  }
};

/** One-time cleanup of legacy cache keys. Runs on module load. */
const cleanupLegacyKeys = () => {
  if (!isBrowser()) return;
  LEGACY_CACHE_KEYS.forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      // no-op
    }
  });
};

cleanupLegacyKeys();
